import { useState, useCallback, useContext, useRef, useEffect } from "react";
import { Stack, StackItem } from "@fluentui/react";
import TextareaAutosize from "react-textarea-autosize";
import { Send } from "../../assets/Send2";
import { SendDisabled } from "../../assets/DisabledSendReg";
import { StopIcon } from "../../assets/Stop";
// import Microphone from "../../assets/greenMic.svg";
import styles from "./QuestionInput.module.css";
// import ReactMarkdown from "react-markdown";
import { AppStateContext } from "../../state/AppProvider";
// import { MicRegular, MicFilled} from "@fluentui/react-icons";
import { SpeechInput } from "./SpeechInput";
import { set } from "lodash";
interface Props {
    onSend: (question: string, id?: string) => void;
    disabled: boolean;
    placeholder?: string;
    clearOnSend?: boolean;
    conversationId?: string;
    speechEnabled?: string;
    stopMsgPlaying: boolean;
    setStopMsgPlaying: (stopMsgPlaying: boolean) => void;
    popUpBlocker: boolean;
    setPopUpBlocker: (popUpBlocker: boolean) => void;
    isContentInsideIframeWindow?: boolean;
    isFullScreen?: boolean;
    isMsgRenderComplete: boolean;
    setIsStopped: (isStopped: boolean) => void;
    characterLimit: number;
    addErrorMessageToChat: (errorMessage: string) => void;
    isDarkMode: boolean;
}

export const QuestionInput = ({ onSend, disabled, placeholder, clearOnSend, conversationId, speechEnabled, stopMsgPlaying, setStopMsgPlaying, popUpBlocker, setPopUpBlocker, isContentInsideIframeWindow, isFullScreen, isMsgRenderComplete, setIsStopped, addErrorMessageToChat, characterLimit, isDarkMode }: Props) => {
    const [question, setQuestion] = useState<string>("");
    const appStateContext = useContext(AppStateContext)
    const branding = appStateContext?.state.frontendSettings?.branding;
    const [microphoneActive, setmicrophoneActive] = useState<boolean>(false);
    const speechWaitingMsg = branding?.speech_waiting_msg ? branding?.speech_waiting_msg : "I'm listening...";
    const speechErrorMsg = branding?.speech_error_msg ? branding?.speech_error_msg : "I'm sorry I am having difficulties understanding. Please ensure your microphone is working properly or select a language above.";
    const inputRef = useRef<HTMLTextAreaElement>(null); 
    const buttonRef = useRef<HTMLDivElement>(null);

    const sendQuestion = () => {
        if(question.length > characterLimit){
            addErrorMessageToChat(`The message you submitted was too long. The max length is ${characterLimit} characters. Please submit something shorter.`);
            return;
        }

        if (disabled || !question.trim() || question.trim() === speechErrorMsg || question.trim() === speechWaitingMsg) {
            return;
        }
        if(conversationId){
            onSend(question, conversationId);
        }else{
            onSend(question);
        }
        if (clearOnSend) {
            setQuestion("");
            // Blur and refocus to dismiss any lingering browser inline autocomplete suggestion
            inputRef.current?.blur();
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    };
    
    
   
    const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
        if (ev.key === "Enter" && !ev.shiftKey  && !(ev.nativeEvent?.isComposing === true)) {
            ev.preventDefault();
            sendQuestion();
        }
    };

    const onQuestionChange = useCallback(
        (_ev: React.ChangeEvent<HTMLTextAreaElement>) => {
          setQuestion(_ev.target.value ?? "");
        },
        [setQuestion],
      );

    const sendQuestionDisabled = disabled || !question.trim() || microphoneActive || question.trim() === speechErrorMsg;
    useEffect(() => {

        //console.log("**Q stopMsgPlaying " + microphoneActive);
        setStopMsgPlaying(microphoneActive);
       

    }, [microphoneActive]);
    
    useEffect(() => {
        if (window.self === window.top) {
            // Set focus on main page when loaded
            inputRef.current?.focus();
        }
    }, []);

    useEffect(() => {
      if (question.trim() === speechErrorMsg){
        setTimeout(() => {
          document.getElementById("speechbutton")?.focus();
        }, 100);
      } 
    }, [question]);

    useEffect(() => {
      if (!microphoneActive && question.trim() && question.trim() !== speechWaitingMsg && question.trim() !== speechErrorMsg){
        document.getElementById("question-input-textarea")?.focus();
      }
    }, [microphoneActive, question]);

    return (
        <Stack horizontal className={microphoneActive ? (isContentInsideIframeWindow ? (isFullScreen ? styles.questionInputContainerFocus : styles.questionInputContainerFocusWidget) : styles.questionInputContainerFocus) : styles.questionInputContainer}>
            <div className={styles.questionInputText}> 
         
             <TextareaAutosize
                id="question-input-textarea"
                ref={inputRef}
                className={styles.questionInputTextArea}
                placeholder={placeholder}
                value={question}
                onChange={onQuestionChange}
                onKeyDown={onEnterPress}
                maxRows={5}
                name="txtQuestionInputText"
                aria-label="Question input"
            />
            </div>
            <div>
                {branding?.question_inputField_character_limit_tracker_enabled.toLowerCase() === 'true' && question.length >= characterLimit/2 && (
                    <p className={styles.textBox}
                    style={{
                        color: question.length > characterLimit ? "var(--YW_Theme_Error_Font_Color)" : isDarkMode ? '#FFFFFF': "inherit",
                    }}
                    >{question.length}/{characterLimit}</p>
                )}
            </div>
            {speechEnabled  === 'Y' &&
              <SpeechInput updateQuestion={setQuestion} microphoneActive={microphoneActive} setmicrophoneActive={setmicrophoneActive} question={question.trim()} popUpBlocker={popUpBlocker} setPopUpBlocker={setPopUpBlocker}/>
            }
            <div ref={buttonRef} className={styles.questionInputSendButtonContainer}
                  onClick={() => {
                    if (!isMsgRenderComplete) {
                        setIsStopped(true); // Stop rendering the message
                    } else if (!popUpBlocker && isMsgRenderComplete) {     
                        sendQuestion(); // Send the question if not stopped
                    }
                }}
                onKeyDown={(e) => {
                    if (!isMsgRenderComplete && (e.key === "Enter" || e.key === " ")) {
                        setIsStopped(true); // Stop rendering the message              
                    } else if (isMsgRenderComplete && !popUpBlocker && (e.key === "Enter" || e.key === " ")) {
                        sendQuestion(); // Send the question if not stopped
                    }
                }}
                style={{ cursor: popUpBlocker ? 'not-allowed' : 'pointer' }}
                aria-label={isMsgRenderComplete ? "Send question" : "Stop question"}
                aria-disabled={isMsgRenderComplete ? (sendQuestionDisabled ? true : false) : false}
                role="button"
                tabIndex={0}
                >
                { isMsgRenderComplete ? (sendQuestionDisabled ? 
                    <StackItem title="Send Question" className={styles.questionInputSendButtonDisabled}>{SendDisabled}</StackItem>
                :   
                  <StackItem title="Send Question" className={styles.questionInputButton}>
                    <Send 
                      color1={getComputedStyle(document.documentElement).getPropertyValue('--YW_Body_Active_Submit_Button_Color').trim()}
                      color2={getComputedStyle(document.documentElement).getPropertyValue('--YW_Body_Active_Submit_Button_Color2').trim()}
                      color3={getComputedStyle(document.documentElement).getPropertyValue('--YW_Body_Active_Submit_Button_Color3').trim()} 
                    />
                  </StackItem>
                )
                   : 
                    <StackItem title="Stop Question" className={styles.questionInputButton}>{StopIcon}</StackItem>
                }
            </div>
            <div
              aria-live="assertive"
              aria-atomic="true"
              className={styles.visuallyHidden}>
              {
                question.trim() === speechErrorMsg
                ? speechErrorMsg 
                : ""
              }
            </div>

           
            {/* <div className={styles.questionInputBottomBorder} /> */}
        </Stack>
       
    );
};
